import React, { useEffect, useMemo, useState } from "react";
import "../styles/schedule.css";
import "../styles/inquiry.css";

const init = {
  name: "",
  phone: "",
  email: "",
  addr: "",
  interests: { free: true, scuba: false, mermaid: false, etc: false },
  license: "yes",
  content: "",
  agree: false,
};

export default function InquiryPage() {
  const [f, setF] = useState(init);
  const [touched, setTouched] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState("");

  const onChange = (key, val) => setF((s) => ({ ...s, [key]: val }));
  const onTouch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const errors = useMemo(() => {
    const e = {};

    if (!f.name.trim()) {
      e.name = "이름을 입력해주세요.";
    }

    const digits = f.phone.replace(/\D/g, "");
    if (!(digits.length === 10 || digits.length === 11)) {
      e.phone = "연락처를 입력해주세요.";
    }

    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) {
      e.email = "이메일 형식이 올바르지 않습니다.";
    }

    if (f.content.length > 4000) {
      e.content = "내용은 4000자 이하로 입력해주세요.";
    }

    if (!f.agree) {
      e.agree = "필수 동의 항목입니다.";
    }

    return e;
  }, [f]);

  const submit = async (e) => {
    e.preventDefault();

    setTouched({
      name: true,
      phone: true,
      email: true,
      content: true,
      agree: true,
    });

    if (Object.keys(errors).length) {
      return;
    }

    const payload = {
      ...f,
      phone: f.phone.replace(/\D/g, ""),
      interests: Object.entries(f.interests)
        .filter(([, v]) => v)
        .map(([k]) => k),
      createdAt: new Date().toISOString(),
      _honeypot: honeypot,
      _elapsedMs: Date.now() - startedAt,
    };

    try {
      setSubmitting(true);

      const res = await fetch("/api/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(await res.text().catch(() => "서버 오류"));
      }

      alert("문의가 등록되었습니다. 감사합니다!");
      setF(init);
      setTouched({});
      setHoneypot("");
    } catch (err) {
      alert(`전송 실패: ${err.message || "서버 오류"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setF(init);
    setTouched({});
    setHoneypot("");
  };

  useEffect(() => {
    const prev = document.body.style.overflow;

    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = prev || "";
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev || "";
      window.removeEventListener("keydown", onKey);
    };
  }, [showModal]);

  return (
    <div className="schedule-page">
      <section className="sched-section">
        <header className="sched-header">
          <h2>강습 문의</h2>
          <p>문의하고 싶은 내용이나 궁금한 점이 있으신가요? 다이빙이지는 언제나 열려 있습니다.</p>
        </header>

        <div className="sched-card inquiry-card">
          <form onSubmit={submit} className="inq-form" noValidate>
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              autoComplete="off"
              tabIndex="-1"
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
              aria-hidden="true"
            />

            <div className="row two">
              <div className="field">
                <label>
                  이름 <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="이름을 입력해주세요"
                  value={f.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  onBlur={() => onTouch("name")}
                />
                {touched.name && errors.name && <div className="err">{errors.name}</div>}
              </div>

              <div className="field">
                <label>
                  연락처(휴대전화) <span className="req">*</span>
                </label>
                <input
                  type="tel"
                  placeholder="‘-’ 제외하고 입력"
                  value={f.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  onBlur={() => onTouch("phone")}
                />
                {touched.phone && errors.phone && <div className="err">{errors.phone}</div>}
              </div>
            </div>

            <div className="row two">
              <div className="field">
                <label>E-mail</label>
                <input
                  type="email"
                  placeholder="example@mail.com"
                  value={f.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  onBlur={() => onTouch("email")}
                />
                {touched.email && errors.email && <div className="err">{errors.email}</div>}
              </div>

              <div className="field">
                <label>주소</label>
                <input
                  type="text"
                  placeholder="○○시 ○○구 까지만 입력"
                  value={f.addr}
                  onChange={(e) => onChange("addr", e.target.value)}
                />
              </div>
            </div>

            <div className="row two">
              <div className="field">
                <label>관심 종목(중복 선택 가능)</label>
                <div className="checks">
                  <label className="chk">
                    <input
                      type="checkbox"
                      checked={f.interests.free}
                      onChange={(e) =>
                        onChange("interests", { ...f.interests, free: e.target.checked })
                      }
                    />
                    <span>프리다이빙</span>
                  </label>

                  <label className="chk">
                    <input
                      type="checkbox"
                      checked={f.interests.scuba}
                      onChange={(e) =>
                        onChange("interests", { ...f.interests, scuba: e.target.checked })
                      }
                    />
                    <span>스쿠버다이빙</span>
                  </label>

                  <label className="chk">
                    <input
                      type="checkbox"
                      checked={f.interests.mermaid}
                      onChange={(e) =>
                        onChange("interests", { ...f.interests, mermaid: e.target.checked })
                      }
                    />
                    <span>머메이드</span>
                  </label>

                  <label className="chk">
                    <input
                      type="checkbox"
                      checked={f.interests.etc}
                      onChange={(e) =>
                        onChange("interests", { ...f.interests, etc: e.target.checked })
                      }
                    />
                    <span>기타</span>
                  </label>
                </div>
              </div>

              <div className="field">
                <label>자격증 유무</label>
                <div className="radios">
                  <label className="rad">
                    <input
                      type="radio"
                      name="license"
                      checked={f.license === "yes"}
                      onChange={() => onChange("license", "yes")}
                    />
                    <span>있음</span>
                  </label>

                  <label className="rad">
                    <input
                      type="radio"
                      name="license"
                      checked={f.license === "no"}
                      onChange={() => onChange("license", "no")}
                    />
                    <span>없음</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>내용</label>
                <textarea
                  rows={8}
                  placeholder="문의 내용을 입력해주세요."
                  value={f.content}
                  onChange={(e) => onChange("content", e.target.value)}
                  onBlur={() => onTouch("content")}
                />
                {touched.content && errors.content && <div className="err">{errors.content}</div>}
              </div>
            </div>

            <div className="row agree-row">
              <label className="chk big">
                <input
                  type="checkbox"
                  checked={f.agree}
                  onChange={(e) => onChange("agree", e.target.checked)}
                  onBlur={() => onTouch("agree")}
                />
                <span>개인정보수집 및 이용에 동의합니다.</span>
                <span className="req">*</span>
              </label>

              <button type="button" className="link" onClick={() => setShowModal(true)}>
                내용보기
              </button>

              {touched.agree && errors.agree && <div className="err under">{errors.agree}</div>}
            </div>

            <div className="row actions">
              <button type="button" className="btn ghost" onClick={reset}>
                취소
              </button>
              <button type="submit" className="btn solid" disabled={submitting}>
                {submitting ? "전송 중..." : "등록"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {showModal && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target.classList.contains("modal-backdrop")) {
              setShowModal(false);
            }
          }}
        >
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">개인정보수집 및 이용동의 안내</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="닫기"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>
                ‘다이빙이지’는 고객님의 개인정보를 중요시하며, 「개인정보보호법」을 준수하고 있습니다.
                본 안내는 개인정보가 어떠한 용도로 이용되고 있으며, 보호를 위해 어떤 조치가 취해지고 있는지 알려드립니다.
              </p>

              <ol className="list">
                <li>
                  <strong>개인정보 수집 및 이용 목적</strong>
                  <ul>
                    <li>문의 사항에 대한 답변 및 상담 이력 관리</li>
                  </ul>
                </li>

                <li>
                  <strong>수집 항목</strong>
                  <ul>
                    <li>필수 항목: 이름, 전화번호</li>
                    <li>선택 항목: 이메일, 주소, 관심 종목, 자격증 유무, 문의 내용</li>
                  </ul>
                </li>

                <li>
                  <strong>보유 및 이용 기간</strong>
                  <ul>
                    <li>이용 목적 달성 시까지 보유하며, 달성 후 지체 없이 파기합니다.</li>
                  </ul>
                </li>

                <li>
                  <strong>동의 거부권 및 불이익</strong>
                  <ul>
                    <li>개인정보 제공에 대한 동의를 거부할 권리가 있으며, 동의 거부 시 문의 접수가 제한될 수 있습니다.</li>
                  </ul>
                </li>
              </ol>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn solid" onClick={() => setShowModal(false)}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}